import * as setup from 'setup';

export interface SeoManifestEntry {
    url: string;
    page_type: 'tool_landing';
    title: string;
    description: string;
    include_in_sitemap: boolean;
    include_in_llms: boolean;
    lastmod: string;
}

interface RouteSeo {
    canonicalUrl: string;
    description: string;
    imageAlt: string;
    imageUrl: string;
    includeInLlms: boolean;
    includeInSitemap: boolean;
    pageType: 'internal' | 'lesson' | 'tool_landing';
    robots: string;
    structuredData: Record<string, unknown> | null;
    title: string;
}

const toolName = 'Innovation Accounting Simulation';
const toolBrand = 'Kromatic';
const toolBaseUrl = 'https://kromatic.com/tools/innovation-accounting-app';
const buildTimestamp =
    process.env.NEXT_PUBLIC_BUILD_TIMESTAMP || process.env.BUILD_TIMESTAMP || new Date().toISOString();

/** True when app is built or run in staging (non-indexable). Set NEXT_PUBLIC_APP_ENV=staging on staging deploy. */
export function isStaging(): boolean {
    return process.env.NEXT_PUBLIC_APP_ENV === 'staging';
}

function normalizeRoutePath(path: string | null | undefined): string {
    if (!path) {
        return '/';
    }

    let normalized = path.trim();
    if (!normalized) {
        return '/';
    }

    if (normalized.startsWith(toolBaseUrl)) {
        normalized = normalized.slice(toolBaseUrl.length) || '/';
    }

    normalized = normalized.split(/[?#]/)[0] || '/';
    if (!normalized.startsWith('/')) {
        normalized = `/${normalized}`;
    }

    return normalized === '//' ? '/' : normalized;
}

function getLessonId(path: string): string | null {
    const normalized = normalizeRoutePath(path);
    if (!normalized.startsWith('/lessons/')) {
        return null;
    }

    const lessonId = normalized.slice('/lessons/'.length).split('/')[0];
    return lessonId || null;
}

function toAbsoluteToolUrl(path: string): string {
    const normalized = normalizeRoutePath(path);
    return normalized === '/' ? toolBaseUrl : `${toolBaseUrl}${normalized}`;
}

function toAbsoluteAssetUrl(path: string | undefined): string {
    if (!path) {
        return `${toolBaseUrl}${setup.landing.illustration}`;
    }

    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }

    return `${toolBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

function decodeHtmlEntities(text: string): string {
    return text
        .replaceAll('&nbsp;', ' ')
        .replaceAll('&amp;', '&')
        .replaceAll('&quot;', '"')
        .replaceAll('&#39;', "'")
        .replaceAll('&apos;', "'")
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('&rightarrow;', 'to');
}

function stripHtml(text: string | undefined): string {
    if (!text) {
        return '';
    }

    return decodeHtmlEntities(text)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function truncate(text: string, maxLength = 160): string {
    if (text.length <= maxLength) {
        return text;
    }

    const truncated = text.slice(0, maxLength).trim();
    const lastSpace = truncated.lastIndexOf(' ');
    return `${(lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated).trim()}...`;
}

function getLandingTitle(): string {
    return `${toolName} | ${toolBrand}`;
}

function getLandingDescription(): string {
    return truncate(stripHtml(setup.general.documentDescription || setup.landing.text), 170);
}

function getLessonTitle(content: IntroContent | WizardContent): string {
    const lessonTitle = stripHtml(content.title) || toolName;
    return `${lessonTitle} | ${toolName} | ${toolBrand}`;
}

function getLessonDescription(content: IntroContent | WizardContent): string {
    const lessonText =
        typeof content.text === 'function' ? setup.general.documentDescription : stripHtml(content.text);

    return truncate(lessonText || getLandingDescription(), 170);
}

export function getRouteSeo(path: string | null | undefined): RouteSeo {
    const normalizedPath = normalizeRoutePath(path);
    const lessonId = getLessonId(normalizedPath);

    if (normalizedPath === '/') {
        return {
            canonicalUrl: toolBaseUrl,
            description: getLandingDescription(),
            imageAlt: setup.landing.illustrationAlt,
            imageUrl: toAbsoluteAssetUrl(setup.landing.illustration),
            includeInLlms: true,
            includeInSitemap: true,
            pageType: 'tool_landing',
            robots: isStaging() ? 'noindex,nofollow' : 'index,follow',
            structuredData: null,
            title: getLandingTitle(),
        };
    }

    if (lessonId) {
        // Lesson routes are reachable only by logged-in players (anonymous
        // visitors and crawlers are redirected to the landing page by the #171
        // auth gate). They are never indexed and carry no crawl-oriented
        // scaffolding (no FAQ schema, sitemap, or LLMs manifest entry); the
        // route SEO here only supplies the in-app document title/description.
        const content = setup.lessons[lessonId];
        const imagePath = content?.image || setup.landing.illustration;
        const imageAlt = content?.imageAlt || setup.landing.illustrationAlt;

        return {
            canonicalUrl: toAbsoluteToolUrl(`/lessons/${lessonId}`),
            description: content ? getLessonDescription(content) : getLandingDescription(),
            imageAlt,
            imageUrl: toAbsoluteAssetUrl(imagePath),
            includeInLlms: false,
            includeInSitemap: false,
            pageType: 'lesson',
            robots: isStaging() ? 'noindex,nofollow' : 'noindex,follow',
            structuredData: null,
            title: content ? getLessonTitle(content) : getLandingTitle(),
        };
    }

    const internalRobots = isStaging() ? 'noindex,nofollow' : 'noindex,follow';
    return {
        canonicalUrl: toAbsoluteToolUrl(normalizedPath),
        description: getLandingDescription(),
        imageAlt: setup.landing.illustrationAlt,
        imageUrl: toAbsoluteAssetUrl(setup.landing.illustration),
        includeInLlms: false,
        includeInSitemap: false,
        pageType: 'internal',
        robots: internalRobots,
        structuredData: null,
        title: getLandingTitle(),
    };
}

export function getSeoManifestEntries(): SeoManifestEntry[] {
    // Only the public tool-root page is crawlable and indexed; per-lesson
    // entries were stripped once the #171 auth gate made lesson pages
    // unreachable by anonymous visitors and crawlers (issue #175).
    const routeSeo = getRouteSeo('/');
    return [
        {
            url: routeSeo.canonicalUrl,
            page_type: 'tool_landing',
            title: routeSeo.title,
            description: routeSeo.description,
            include_in_sitemap: routeSeo.includeInSitemap,
            include_in_llms: routeSeo.includeInLlms,
            lastmod: buildTimestamp,
        },
    ];
}

export function getSeoManifestPayload(): { entries: SeoManifestEntry[]; generated_at: string; tool: string; version: number } {
    return {
        entries: getSeoManifestEntries(),
        generated_at: buildTimestamp,
        tool: 'innovation-accounting-app',
        version: 1,
    };
}
